import type { FreemiumUsageState } from "@/lib/freemium/usage-state";

export const GUEST_USAGE_TIME_KEY = "guestInteractivePodcastUsageTime";
export const GUEST_USAGE_TODAY_KEY = "guestInteractivePodcastUsageToday";
export const GUEST_FREEMIUM_CARRYOVER_APPLIED_KEY = "guestDemoFreemiumCarryoverApplied";
export const MAX_REGISTERED_SECONDS_PER_DAY = 10 * 60;

const DEVICE_USAGE_TIME_KEY = "deviceDailyUsageTime";
const FREEMIUM_PRE_SESSION_OFFER_DISMISSED_KEY = "chicky:freemium_pre_session_offer_dismissed";
const FREEMIUM_DEBUG_STORAGE_KEY = "chicky:freemium_debug";

export type FreemiumDebugMode =
  | "pre_session_offer"
  | "medium_pre_session_offer"
  | "heavy_pre_session_offer"
  | "daily_limit"
  | "weekly_limit";

type UsageStorageLogger = {
  warn: (message: string, data?: unknown) => void;
  error: (message: string, data?: unknown) => void;
};

export function formatDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function getTodayKeyDate(): string {
  return formatDateKey(new Date());
}

export function getGuestTotalUsageSeconds(): number {
  try {
    const raw = localStorage.getItem(GUEST_USAGE_TIME_KEY);
    const seconds = Number(raw || "0");
    if (!Number.isFinite(seconds) || seconds < 0) return 0;
    return seconds;
  } catch {
    return 0;
  }
}

export function getDateScopedUsageSeconds(key: string, logger?: UsageStorageLogger): number {
  try {
    const today = getTodayKeyDate();
    const raw = localStorage.getItem(key);
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as { date?: unknown; seconds?: unknown };

    if (
      !parsed ||
      typeof parsed.date !== "string" ||
      typeof parsed.seconds !== "number" ||
      !Number.isFinite(parsed.seconds) ||
      parsed.seconds < 0 ||
      parsed.seconds > 86400
    ) {
      localStorage.setItem(key, JSON.stringify({ date: today, seconds: 0 }));
      return 0;
    }

    if (parsed.date !== today) {
      localStorage.setItem(key, JSON.stringify({ date: today, seconds: 0 }));
      return 0;
    }

    return parsed.seconds;
  } catch (error) {
    logger?.error("Error reading date-scoped usage data:", error);
    return 0;
  }
}

export function setDateScopedUsageSeconds(key: string, seconds: number) {
  try {
    const today = getTodayKeyDate();
    const safeSeconds = Math.max(0, Math.min(Math.floor(seconds), 86400));
    localStorage.setItem(key, JSON.stringify({ date: today, seconds: safeSeconds }));
  } catch {}
}

export function incrementGuestUsageSeconds(logger?: UsageStorageLogger): number {
  const newTotalUsage = getGuestTotalUsageSeconds() + 1;
  localStorage.setItem(GUEST_USAGE_TIME_KEY, String(newTotalUsage));
  setDateScopedUsageSeconds(
    GUEST_USAGE_TODAY_KEY,
    getDateScopedUsageSeconds(GUEST_USAGE_TODAY_KEY, logger) + 1
  );
  return newTotalUsage;
}

export function wasUserCreatedToday(createdAt?: string): boolean {
  if (!createdAt) return false;
  const createdDate = new Date(createdAt);
  if (Number.isNaN(createdDate.getTime())) return false;
  return formatDateKey(createdDate) === getTodayKeyDate();
}

export function getRegisteredUsageSeconds(
  _appUserId?: string,
  logger?: UsageStorageLogger
): number {
  try {
    const raw = localStorage.getItem(DEVICE_USAGE_TIME_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as { date: string; seconds: number };
    const today = getTodayKeyDate();

    if (!parsed || typeof parsed.date !== "string" || typeof parsed.seconds !== "number") {
      logger?.warn("Invalid usage data structure, resetting");
      localStorage.setItem(DEVICE_USAGE_TIME_KEY, JSON.stringify({ date: today, seconds: 0 }));
      return 0;
    }

    if (parsed.date !== today) {
      localStorage.setItem(DEVICE_USAGE_TIME_KEY, JSON.stringify({ date: today, seconds: 0 }));
      return 0;
    }

    if (parsed.seconds < 0 || parsed.seconds > 86400) {
      logger?.warn("Invalid usage seconds value, resetting");
      localStorage.setItem(DEVICE_USAGE_TIME_KEY, JSON.stringify({ date: today, seconds: 0 }));
      return 0;
    }

    return parsed.seconds;
  } catch (error) {
    logger?.error("Error reading usage data:", error);
    return 0;
  }
}

export function setRegisteredUsageSeconds(_appUserId?: string, seconds?: number) {
  try {
    const today = getTodayKeyDate();
    localStorage.setItem(
      DEVICE_USAGE_TIME_KEY,
      JSON.stringify({ date: today, seconds: seconds ?? 0 })
    );
  } catch {}
}

export function getFreemiumDebugMode(): FreemiumDebugMode | null {
  if (typeof window === "undefined") return null;

  const isLocalHost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "::1";
  const isExplicitlyEnabled =
    process.env.NEXT_PUBLIC_ENABLE_FREEMIUM_DEBUG?.toLowerCase() === "true";
  if (!isLocalHost && !isExplicitlyEnabled) return null;

  const params = new URLSearchParams(window.location.search);
  const raw = params.get("freemiumDebug") || localStorage.getItem(FREEMIUM_DEBUG_STORAGE_KEY);
  if (
    raw === "pre_session_offer" ||
    raw === "medium_pre_session_offer" ||
    raw === "heavy_pre_session_offer" ||
    raw === "daily_limit" ||
    raw === "weekly_limit"
  ) {
    return raw;
  }
  return null;
}

export function buildDebugFreemiumUsageState(
  overrides?: Partial<FreemiumUsageState>
): FreemiumUsageState {
  return {
    qualifiedConversationCount: 10,
    qualifiedConversationsThisWeek: 1,
    dailyAllowanceMinutes: 10,
    weeklyConversationAllowance: 3,
    dailyRemainingMinutes: 10,
    weeklyRemainingConversations: 2,
    cohort: "heavy_unconverted",
    offerMode: "discount",
    shouldShowPreSessionOffer: true,
    ...overrides,
  };
}

export function wasPreSessionOfferDismissedToday() {
  try {
    return localStorage.getItem(FREEMIUM_PRE_SESSION_OFFER_DISMISSED_KEY) === getTodayKeyDate();
  } catch {
    return false;
  }
}

export function markPreSessionOfferDismissedToday() {
  try {
    localStorage.setItem(FREEMIUM_PRE_SESSION_OFFER_DISMISSED_KEY, getTodayKeyDate());
  } catch {}
}
